import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, CheckCircle, Users, Trophy, Target, Heart } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DISCIPLINES = ['Heading', 'Heeling', 'Breakaway', 'Ranch Roping'];

const ApplyCoachPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    region: '',
    disciplines: [],
    experience: '',
    why_coach: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleDisciplineChange = (discipline, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        disciplines: [...prev.disciplines, discipline]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        disciplines: prev.disciplines.filter(d => d !== discipline)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (!formData.name || !formData.email || !formData.region || !formData.experience || !formData.why_coach) {
      setError('Please fill in all required fields.');
      setLoading(false);
      return;
    }

    if (formData.disciplines.length === 0) {
      setError('Please select at least one discipline.');
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${API_URL}/api/coaches/apply`, formData);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-12 pb-8">
            <div className="w-20 h-20 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-secondary" />
            </div>
            <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-4">
              Application Submitted!
            </h2>
            <p className="text-muted-foreground mb-8">
              Thank you for your interest in coaching with Elevated Roping Media. We'll review your application and get back to you soon.
            </p>
            <Link to="/">
              <Button className="rounded-full" data-testid="back-home-btn">
                Back to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-12 md:py-16 overflow-hidden bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/coaches" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Meet the Coaches
          </Link>
          <h1 className="font-display font-black text-3xl md:text-5xl tracking-tight uppercase text-foreground leading-none mb-4">
            Become a Coach
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Join our team of professional coaches and help ropers improve their skills. Share your knowledge and passion for the sport.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display font-bold text-xl uppercase tracking-wide text-center mb-8">
            Why Coach With Us?
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <Trophy className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-1">Share Your Expertise</h3>
              <p className="text-sm text-muted-foreground">Help athletes at all levels improve</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="font-semibold mb-1">Flexible Schedule</h3>
              <p className="text-sm text-muted-foreground">Review videos on your own time</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-3">
                <Target className="w-6 h-6 text-accent" />
              </div>
              <h3 className="font-semibold mb-1">Earn Income</h3>
              <p className="text-sm text-muted-foreground">Get paid for each critique you provide</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <Heart className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-1">Give Back</h3>
              <p className="text-sm text-muted-foreground">Support the roping community</p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-12 bg-muted/30">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardHeader>
              <CardTitle className="font-display text-xl uppercase">Coach Application</CardTitle>
              <CardDescription>
                Tell us about yourself and your experience. We'll review your application and reach out if there's a good fit.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert variant="destructive" className="mb-6">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your full name"
                    required
                    data-testid="coach-apply-name"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@example.com"
                    required
                    data-testid="coach-apply-email"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number (Optional)</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(403) 555-0123"
                    data-testid="coach-apply-phone"
                  />
                </div>

                {/* Region */}
                <div className="space-y-2">
                  <Label htmlFor="region">Region / Location *</Label>
                  <Input
                    id="region"
                    value={formData.region}
                    onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                    placeholder="e.g., Medicine Hat, Alberta"
                    required
                    data-testid="coach-apply-region"
                  />
                </div>

                {/* Disciplines */}
                <div className="space-y-3">
                  <Label>Disciplines You Can Coach *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {DISCIPLINES.map(discipline => (
                      <div key={discipline} className="flex items-center space-x-2">
                        <Checkbox
                          id={`discipline-${discipline}`}
                          checked={formData.disciplines.includes(discipline)}
                          onCheckedChange={(checked) => handleDisciplineChange(discipline, checked)}
                          data-testid={`coach-apply-discipline-${discipline.toLowerCase()}`}
                        />
                        <Label htmlFor={`discipline-${discipline}`} className="cursor-pointer">
                          {discipline}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Experience */}
                <div className="space-y-2">
                  <Label htmlFor="experience">Competitive Experience *</Label>
                  <Textarea
                    id="experience"
                    value={formData.experience}
                    onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                    placeholder="Tell us about your competitive background, achievements, and years of experience..."
                    rows={4}
                    required
                    data-testid="coach-apply-experience"
                  />
                  <p className="text-xs text-muted-foreground">
                    Include any notable achievements, qualifications, or events you've competed in.
                  </p>
                </div>

                {/* Why Coach */}
                <div className="space-y-2">
                  <Label htmlFor="why_coach">Why Do You Want to Coach? *</Label>
                  <Textarea
                    id="why_coach"
                    value={formData.why_coach}
                    onChange={(e) => setFormData({ ...formData, why_coach: e.target.value })}
                    placeholder="What motivates you to help other ropers improve? What's your coaching philosophy?"
                    rows={4}
                    required
                    data-testid="coach-apply-why"
                  />
                </div>

                {/* Submit */}
                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold uppercase tracking-wide"
                    data-testid="coach-apply-submit"
                  >
                    {loading ? 'Submitting...' : 'Submit Application'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/zh1vb354_Elevated%20Logo.png" 
                alt="Elevated Roping Media" 
                className="h-8 w-auto"
              />
              <span className="font-display font-bold tracking-tight text-background/90">ELEVATED ROPING MEDIA</span>
            </Link>
            <p className="text-background/60 text-xs">
              © {new Date().getFullYear()} Elevated Roping Media Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ApplyCoachPage;
